import React, { useState } from 'react';
import SectionTitle from '@/components/SectionTitle';
import Seo from '@/seo/Seo';
import { PAGE_SEO } from '@/seo/seoConfig';
import { breadcrumbSchema } from '@/seo/schemas';
import { Mail, Phone, MapPin, Send, MessageCircle, HandHeart, Youtube } from 'lucide-react';
import { CHURCH } from '@/api/client';
import { toast } from 'sonner';
import api from '@/api/client';

const MESSAGE_TYPES = ['General Enquiry','Prayer Request','Pastoral Care','Sunday School','Summer Camp','Evangelism','Volunteering'];

export default function Contact() {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', message_type: 'General Enquiry', message: '', consent_given: false });
  const [submitting, setSubmitting] = useState(false);
  const [prayerForm, setPrayerForm] = useState({ full_name: '', email: '', request: '', is_anonymous: false });
  const [prayerSubmitting, setPrayerSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent_given) return toast.error('Please consent to be contacted');
    setSubmitting(true);
    try {
      const { data } = await api.post('/public/contact', form);
      toast.success(data.message || 'Message sent.');
      setForm({ full_name: '', email: '', phone: '', message_type: 'General Enquiry', message: '', consent_given: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send. Please try again.');
    } finally { setSubmitting(false); }
  };

  const onSubmitPrayer = async (e) => {
    e.preventDefault();
    if (!prayerForm.request.trim()) return toast.error('Please share your prayer request');
    setPrayerSubmitting(true);
    try {
      const { data } = await api.post('/public/prayer-request', prayerForm);
      toast.success(data.message || 'Prayer request received.');
      setPrayerForm({ full_name: '', email: '', request: '', is_anonymous: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit. Please try again.');
    } finally { setPrayerSubmitting(false); }
  };

  return (
    <div>
      <Seo
        title={PAGE_SEO.contact.title}
        titleExact
        description={PAGE_SEO.contact.description}
        keywords={PAGE_SEO.contact.keywords}
        path="/contact"
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }])]}
      />
      <section className="gradient-purple text-white py-12 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-moj-gold text-sm font-bold uppercase tracking-[0.3em] mb-3">Get in touch</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-white mb-3">We’d love to hear from you</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">Send a message, request prayer, or simply say hello — our team responds with care.</p>
        </div>
      </section>

      <section className="py-16 bg-moj-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-3 gap-6">
          <div className="card-moj p-6"><MapPin size={28} className="text-moj-gold-deep mb-2"/><h3 className="font-serif text-lg mb-1">Our Address</h3><p className="text-sm text-moj-charcoal/80">{CHURCH.address}</p></div>
          <div className="card-moj p-6"><Phone size={28} className="text-moj-gold-deep mb-2"/><h3 className="font-serif text-lg mb-1">Phone</h3><a href={`tel:${CHURCH.phone.replace(/\s/g,'')}`} className="text-sm text-moj-purple hover:underline">{CHURCH.phone}</a></div>
          <div className="card-moj p-6"><Mail size={28} className="text-moj-gold-deep mb-2"/><h3 className="font-serif text-lg mb-1">Email</h3><a href={`mailto:${CHURCH.email}`} className="text-sm text-moj-purple hover:underline break-all">{CHURCH.email}</a></div>
        </div>
      </section>

      <section className="py-16 gradient-soft-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10">
          <div>
            <SectionTitle kicker="Contact Form" title="Send us a message" />
            <form onSubmit={onSubmit} className="card-moj p-6 sm:p-8 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="label-moj" htmlFor="cf-name">Full Name *</label><input id="cf-name" required autoComplete="name" className="input-moj" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})}/></div>
                <div><label className="label-moj" htmlFor="cf-email">Email *</label><input id="cf-email" type="email" inputMode="email" autoComplete="email" required className="input-moj" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}/></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="label-moj" htmlFor="cf-phone">Phone</label><input id="cf-phone" type="tel" inputMode="tel" autoComplete="tel" className="input-moj" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}/></div>
                <div><label className="label-moj" htmlFor="cf-type">Message Type *</label>
                  <select id="cf-type" required className="input-moj" value={form.message_type} onChange={(e) => setForm({...form, message_type: e.target.value})}>
                    {MESSAGE_TYPES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div><label className="label-moj" htmlFor="cf-msg">Message *</label><textarea id="cf-msg" required rows={5} className="input-moj" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})}/></div>
              <label className="flex items-start gap-3 text-sm text-moj-charcoal cursor-pointer py-2 -my-2 touch-manipulation">
                <input type="checkbox" checked={form.consent_given} onChange={(e) => setForm({...form, consent_given: e.target.checked})} className="mt-0.5 h-5 w-5 shrink-0 accent-moj-purple cursor-pointer"/>
                <span className="leading-relaxed">I consent to Ministry of Jesus contacting me about my enquiry. See our <a href="/privacy" className="text-moj-purple underline">Privacy Notice</a>.</span>
              </label>
              <button disabled={submitting} className="btn-primary w-full justify-center" data-testid="contact-submit"><Send size={18}/> {submitting ? 'Sending…' : 'Send Message'}</button>
            </form>
          </div>

          <div>
            <SectionTitle kicker="Prayer Request" title="Submit a prayer request" />
            <form onSubmit={onSubmitPrayer} className="card-moj p-6 sm:p-8 space-y-4" data-testid="contact-prayer-form">
              <div><label className="label-moj" htmlFor="pr-name">Full Name (optional)</label><input id="pr-name" autoComplete="name" className="input-moj" value={prayerForm.full_name} onChange={(e) => setPrayerForm({...prayerForm, full_name: e.target.value})}/></div>
              <div><label className="label-moj" htmlFor="pr-email">Email (optional)</label><input id="pr-email" type="email" inputMode="email" autoComplete="email" className="input-moj" value={prayerForm.email} onChange={(e) => setPrayerForm({...prayerForm, email: e.target.value})}/></div>
              <div><label className="label-moj" htmlFor="pr-req">Prayer Request *</label><textarea id="pr-req" required rows={5} className="input-moj" value={prayerForm.request} onChange={(e) => setPrayerForm({...prayerForm, request: e.target.value})} placeholder="Lord, please..."/></div>
              <label className="flex items-center gap-3 text-sm text-moj-charcoal cursor-pointer py-2 -my-2 touch-manipulation">
                <input type="checkbox" checked={prayerForm.is_anonymous} onChange={(e) => setPrayerForm({...prayerForm, is_anonymous: e.target.checked})} className="h-5 w-5 shrink-0 accent-moj-purple cursor-pointer"/>
                <span>Keep my request anonymous</span>
              </label>
              <button disabled={prayerSubmitting} className="btn-gold w-full justify-center" data-testid="prayer-submit-contact"><HandHeart size={18}/> {prayerSubmitting ? 'Sending…' : 'Submit Prayer Request'}</button>
            </form>
          </div>
        </div>
      </section>

      <section className="py-12 bg-moj-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="card-moj overflow-hidden">
            <iframe
              title="Church location map"
              src={`https://www.google.com/maps?q=${encodeURIComponent(CHURCH.address)}&output=embed`}
              className="w-full h-96 border-0"
              loading="lazy"
            />
          </div>
          <div className="flex justify-center gap-4 mt-6">
            <a href={CHURCH.youtube} target="_blank" rel="noopener noreferrer" className="btn-outline"><Youtube size={16}/> Subscribe on YouTube</a>
            <a href={`mailto:${CHURCH.email}`} className="btn-outline"><Mail size={16}/> Email Us</a>
          </div>
        </div>
      </section>
    </div>
  );
}
